import TableSplit20 from '@carbon/icons-react/es/table--split/20';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import { Dropdown } from 'antd';
import React, { FC, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Menu } from 'components/atoms/dropdown';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { ToolButtonWrapper } from '../tool-button-wrapper';
import styles from './insert-table.module.css';

interface Props {
  editor: Editor;
}

export const InsertTable: FC<Props> = ({ editor }: Props) => {
  const [currentCol, setCurrentCol] = useState(0);
  const [currentRow, setCurrentRow] = useState(0);
  const [isVisible, setDropdownVisibility] = useState(false);
  const isActive = (rowId, colId) => rowId <= currentRow && colId <= currentCol;
  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();

    editor.model.change((writer) => {
      const { selection } = editor.model.document;

      // Selection is not on an empty line or not on the first char position
      if (
        selection.getFirstPosition().offset !== 0 &&
        selection.getLastPosition().offset !== 0
      ) {
        // Do an 'enter' here to start a new line, so that selection setting for the new paragraph we're creating will work properly
        editor.execute('enter');
      }

      // We need an element ref for setting selection properly. So create a p here and refer that to set selection
      const paragraph = writer.createElement('paragraph');

      editor.model.insertContent(paragraph);

      writer.setSelection(paragraph, 'before');

      editor.execute('insertTable', {
        rows: currentRow + 1,
        columns: currentCol + 1,
      });

      editor.editing.view.focus();
    });

    setCurrentCol(0);
    setCurrentRow(0);
    setDropdownVisibility(false);
  };
  // TODO: Optimize this with a debounce.
  const handleMouseOver = (e) => {
    setCurrentCol(parseInt(e.target.dataset.colid, 10));
    setCurrentRow(parseInt(e.target.dataset.rowid, 10));
  };
  const intl = useIntl();
  const dropdownMenu = (
    <Menu>
      {Array.from(Array(10).keys()).map((rowId) => (
        <div key={rowId} className={styles.row}>
          {Array.from(Array(10).keys()).map((colId) => (
            <button
              data-colid={colId}
              data-rowid={rowId}
              key={colId}
              aria-label={intl.formatMessage({ id: 'table.create' })}
              className={`${styles.col} ${
                isActive(rowId, colId) ? styles.active : ''
              }`}
              onMouseOver={handleMouseOver}
              onFocus={handleMouseOver}
              onClick={handleClick}
              type="button"
            />
          ))}
        </div>
      ))}
      <p className={styles.content}>
        {currentRow + 1} <FormattedMessage id="table.rows" /> {currentCol + 1}{' '}
        <FormattedMessage id="table.cols" />
      </p>
    </Menu>
  );
  const onVisibleChange = (visibility) => {
    setDropdownVisibility(visibility);
  };

  return (
    <ToolButtonWrapper>
      <Dropdown
        overlay={dropdownMenu}
        onVisibleChange={onVisibleChange}
        visible={isVisible}
      >
        <IconWrapper>
          <TableSplit20 />
        </IconWrapper>
      </Dropdown>
    </ToolButtonWrapper>
  );
};
